/**
 * FileDropzone — Wiederverwendbare Drag-&-Drop-Upload-Zone.
 *
 * Funktioniert als Drop-In-Ersatz fuer ein <input type="file"> + Label-Block.
 *  - Drag-&-Drop und Klick zum Auswaehlen
 *  - Visuelles Feedback bei Hover / DragOver / Upload / Erfolg
 *  - Ruft den uebergebenen onUpload-Handler mit einem synthetischen Event auf,
 *    sodass bestehende handleXyzUpload(e)-Funktionen weiter funktionieren.
 *  - Akzeptiert sowohl `accept`-MIME-Strings als auch Dateiendungen.
 */
import React, { useRef, useState, useCallback } from 'react';
import { Loader2, UploadCloud, FileCheck } from 'lucide-react';

export const FileDropzone = ({
  accept = '.pdf,.jpg,.jpeg,.png',
  onUpload,
  uploading = false,
  filename = '',
  disabled = false,
  testId,
  hint = 'PDF, JPG oder PNG · max. 10 MB',
  multiple = false,
}) => {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const triggerUpload = useCallback((files) => {
    if (!files || files.length === 0 || !onUpload) return;
    // synthetisches Event so bauen, dass bestehende Handler (e.target.files,
    // e.target.value = '') weiter funktionieren
    const fakeEvent = {
      target: {
        files,
        value: '',
      },
      preventDefault: () => {},
      stopPropagation: () => {},
    };
    onUpload(fakeEvent);
  }, [onUpload]);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled || uploading) return;
    triggerUpload(e.dataTransfer.files);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || uploading) return;
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleClick = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && !uploading) {
      e.preventDefault();
      handleClick();
    }
  };

  const stateClasses = uploading
    ? 'border-primary/40 bg-primary/5'
    : dragActive
      ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
      : filename
        ? 'border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10'
        : 'border-border hover:border-primary/40 hover:bg-secondary/40';

  return (
    <div>
      <button
        type="button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        disabled={disabled || uploading}
        className={`w-full text-left rounded-lg border-2 border-dashed p-4 transition-all flex items-center gap-3 cursor-pointer ${stateClasses} ${disabled || uploading ? 'opacity-70 cursor-wait' : ''}`}
        data-testid={testId}
      >
        <div className={`shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${filename ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-primary/10 text-primary'}`}>
          {uploading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : filename ? (
            <FileCheck className="h-5 w-5" />
          ) : (
            <UploadCloud className="h-5 w-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {uploading ? (
            <p className="text-sm font-medium text-primary">Wird hochgeladen…</p>
          ) : filename ? (
            <>
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400 truncate">{filename}</p>
              <p className="text-xs text-muted-foreground">Klicken oder Datei ablegen, um zu ersetzen</p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium">
                {dragActive ? 'Datei jetzt ablegen' : 'Datei hier ablegen oder klicken'}
              </p>
              <p className="text-xs text-muted-foreground">{hint}</p>
            </>
          )}
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => triggerUpload(e.target.files)}
        disabled={disabled || uploading}
        data-testid={testId ? `${testId}-input` : undefined}
      />
    </div>
  );
};
